import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar as CalendarIcon, Video, BookOpen } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function JournalTimeline() {
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const { theme } = useTheme();
  const [timeline, setTimeline] = useState({});
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTimeline();
  }, []);

  const fetchTimeline = async () => {
    setLoading(true);
    try {
      // Use the grouped endpoint with longer timeout
      const response = await axios.get(`${API}/journal/entries/grouped`, {
        headers: getAuthHeader(),
        timeout: 10000  // 10 second timeout
      });
      
      const grouped = response.data.grouped || {};
      setTimeline(grouped);
      
      // Auto-select current year if exists
      const currentYear = new Date().getFullYear().toString();
      if (grouped[currentYear]) {
        setSelectedYear(currentYear);
      } else {
        // Select most recent year
        const years = Object.keys(grouped).sort().reverse();
        if (years.length > 0) {
          setSelectedYear(years[0]);
        }
      }
      
      // Don't show error if data is just empty
      if (Object.keys(grouped).length === 0) {
        console.log('No journal entries found (empty state is correct)');
      }
    } catch (error) {
      console.error('Timeline fetch error:', error);
      // Only show error if it's a real error, not timeout or empty data
      if (error.code !== 'ECONNABORTED') {
        toast.error('Timeline is loading slowly. Please wait...');
      }
      setTimeline({});
    } finally {
      setLoading(false);
    }
  };

  const monthNames = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];
  const monthLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  return (
    <div className="min-h-screen" style={{ 
      background: theme === 'dark' 
        ? 'linear-gradient(135deg, #0a0e27 0%, #1a1f3a 100%)' 
        : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
    }}>
      <div className={`backdrop-blur-md ${theme === 'dark' ? 'bg-white/5' : 'bg-black/5'} border-b ${theme === 'dark' ? 'border-white/10' : 'border-black/10'}`}>
        <div className="max-w-7xl mx-auto px-4 py-4">
          <button 
            onClick={() => navigate('/player-dashboard')} 
            className={`flex items-center ${theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'} mb-4`}
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Dashboard
          </button>
          <h1 className={`text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Cricket Journey Timeline</h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="spinner"></div>
          </div>
        ) : (
          <>
        {/* Year Selector */}
        <div className="flex items-center space-x-4 mb-8">
          {Object.keys(timeline).sort().reverse().map(year => (
            <Button
              key={year}
              variant={selectedYear === year ? 'default' : 'outline'}
              onClick={() => {setSelectedYear(year); setSelectedMonth(null);}}
            >
              {year}
            </Button>
          ))}
        </div>

        {/* Month Grid */}
        {selectedYear && timeline[selectedYear] && (
          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
            {monthNames.map((month, idx) => {
              const hasData = timeline[selectedYear] && timeline[selectedYear][month];
              
              // Count journals in this month (entries are arrays now, not objects with .journals)
              let journalCount = 0;
              if (hasData) {
                Object.values(timeline[selectedYear][month]).forEach(dayEntries => {
                  journalCount += Array.isArray(dayEntries) ? dayEntries.length : 0;
                });
              }
              
              return (
                <Card
                  key={month}
                  className={`glass-card cursor-pointer ${selectedMonth === month ? 'ring-2 ring-green-500' : ''} ${theme === 'light' ? 'bg-white/95' : ''} ${!hasData ? 'opacity-50' : 'hover:scale-105'} transition-all`}
                  onClick={() => hasData && setSelectedMonth(month)}
                >
                  <div className="text-center py-4">
                    <div className={`font-bold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{monthLabels[idx]}</div>
                    {hasData && (
                      <div className="space-y-1">
                        <div className="flex items-center justify-center space-x-1 text-xs">
                          <BookOpen className="w-3 h-3 text-blue-400" />
                          <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>{journalCount}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* Day View */}
        {selectedYear && selectedMonth && timeline[selectedYear][selectedMonth] && (
          <div>
            <h2 className={`text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              {monthLabels[parseInt(selectedMonth) - 1]} {selectedYear}
            </h2>
            <div className="space-y-4">
              {Object.keys(timeline[selectedYear][selectedMonth]).sort().reverse().map(day => {
                const dayEntries = timeline[selectedYear][selectedMonth][day];
                const journals = Array.isArray(dayEntries) ? dayEntries : [];
                
                return (
                  <Card key={day} className={`glass-card ${theme === 'light' ? 'bg-white/95' : ''}`}>
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-2">
                        <CalendarIcon className="w-5 h-5 text-green-400" />
                        <h3 className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                          Day {day}
                        </h3>
                      </div>
                      <div className="flex items-center space-x-4 text-sm">
                        <span className={theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}>
                          {journals.length} journal{journals.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>

                    {/* Journals */}
                    {journals.map(journal => (
                      <div 
                        key={journal.id}
                        onClick={() => navigate(`/journal/${journal.id}`)}
                        className={`p-4 rounded-lg mb-3 cursor-pointer ${theme === 'dark' ? 'bg-white/5 hover:bg-white/10' : 'bg-gray-100 hover:bg-gray-200'} transition-colors`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className={`font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{journal.session_type}</span>
                          <span className={`text-sm ${theme === 'dark' ? 'text-gray-500' : 'text-gray-600'}`}>{journal.duration_minutes} min</span>
                        </div>
                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-700'}`}>{journal.what_practiced}</p>
                      </div>
                    ))}
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {!selectedMonth && selectedYear && (
          <div className="text-center py-16">
            <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Select a month to view your cricket journey</p>
          </div>
        )}
        
        {Object.keys(timeline).length === 0 && !loading && (
          <div className="text-center py-16">
            <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-4`}>No journal entries yet</p>
            <Button onClick={() => navigate('/journal/new')} className="bg-gradient-to-r from-green-500 to-emerald-600">
              Create Your First Journal
            </Button>
          </div>
        )}
          </>
        )}
      </div>
    </div>
  );
}
